import React, { useContext, useEffect, useRef } from "react";
import { Link, useLocation, matchPath } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Palette,
  Globe,
  Video,
  Flag,
  Grid,
  Settings,
  X,
  Cpu,
  Mail,
  Link2,
  Activity,
} from "lucide-react";
import { AccountContext } from "../account/account";
import grigoraLogo from "../assets/grigora-logo.png";

function NavItem({ to, icon: Icon, label, paths = [], onClick }) {
  const location = useLocation();
  const allPaths = [to, ...paths];
  const isActive = allPaths.some((p) => matchPath(p, location.pathname));

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg transition-all duration-200 group mb-1 ${
        isActive
          ? "bg-[#623ce7] text-white shadow-md shadow-[#623ce7]/30"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`}
    >
      <Icon
        size={18}
        className={`${isActive ? "text-white" : "text-slate-400 group-hover:text-slate-600"}`}
      />
      <span className="font-medium tracking-wide text-sm">{label}</span>
    </Link>
  );
}

function NavSection({ title }) {
  return (
    <div className="px-3.5 mt-5 mb-1.5">
      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
        {title}
      </span>
    </div>
  );
}

export default function Sidebar({ mobileOpen = false, onMobileClose }) {
  const location = useLocation();
  const { userAttributes } = useContext(AccountContext) || {};

  const displayName =
    userAttributes?.name ||
    userAttributes?.given_name ||
    (userAttributes?.email ? userAttributes.email.split("@")[0] : "Admin");
  const displaySubtext = userAttributes?.email || "Grigora Admin";
  const userInitial = displayName.charAt(0).toUpperCase();

  // Close drawer on path change
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (mobileOpen && prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      onMobileClose?.();
    } else {
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, mobileOpen, onMobileClose]);

  // Handle escape key
  useEffect(() => {
    if (!mobileOpen) return;
    const handleEscape = (e) => {
      if (e.key === "Escape") onMobileClose?.();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [mobileOpen, onMobileClose]);

  const sidebarNavContent = (onItemClick) => (
    <>
      {/* Brand Header */}
      <div className="p-5 flex items-center gap-3 border-b border-slate-100 shrink-0">
        <div className="w-8 h-8 flex items-center justify-center shrink-0">
          <img
            src={grigoraLogo}
            alt="Grigora Logo"
            className="w-8 h-8 object-contain"
          />
        </div>
        <span className="text-xl font-bold text-slate-900 leading-none">
          Grigora <span className="text-[#623ce7] text-xs align-top font-semibold">Admin</span>
        </span>
      </div>

      {/* Navigation List */}
      <nav className="flex-1 px-3 py-3 overflow-y-auto scrollbar-hide">
        <NavItem to="/" icon={LayoutDashboard} label="Dashboard" paths={["/"]} onClick={onItemClick} />
        <NavItem to="/users" icon={Users} label="Users" paths={["/users", "/users/:sub"]} onClick={onItemClick} />
        <NavItem to="/appsumo" icon={Activity} label="AppSumo Activity" onClick={onItemClick} />
        <NavItem to="/ai-usage" icon={Cpu} label="AI Usage" paths={["/ai-usage"]} onClick={onItemClick} />

        <NavItem to="/checkout-reminders" icon={Mail} label="Checkout Reminders" onClick={onItemClick} />
        <NavItem to="/email-analytics" icon={Mail} label="Email Analytics" onClick={onItemClick} />

        <NavSection title="Design & Content" />
        <NavItem to="/themes" icon={Palette} label="Themes" paths={["/themes", "/themes/add", "/themes/:themeID"]} onClick={onItemClick} />
        <NavItem to="/projects" icon={Globe} label="Websites" paths={["/projects", "/projects/:projectID", "/projects/:projectID/*"]} onClick={onItemClick} />
        <NavItem to="/domains" icon={Link2} label="Domains" onClick={onItemClick} />

        <NavSection title="Marketing & Apps" />
        <NavItem to="/webinars" icon={Video} label="Webinars" paths={["/webinars"]} onClick={onItemClick} />
        <NavItem to="/banners" icon={Flag} label="Banners" paths={["/banners", "/banners/add", "/banners/:bannerID"]} onClick={onItemClick} />
        <NavItem to="/apps" icon={Grid} label="App Store" paths={["/apps", "/apps/:appID"]} onClick={onItemClick} />

        <NavSection title="System" />
        <NavItem to="/settings" icon={Settings} label="Settings" paths={["/settings", "/settings/*"]} onClick={onItemClick} />
      </nav>

      {/* User Info Footer */}
      <div className="p-4 border-t border-slate-200 shrink-0">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg bg-slate-50 border border-slate-100">
          <div className="w-8 h-8 rounded-full bg-[#623ce7]/10 text-[#623ce7] font-semibold text-xs flex items-center justify-center border border-slate-200 shrink-0">
            {userInitial}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{displayName}</p>
            <p className="text-xs text-slate-500 truncate">{displaySubtext}</p>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar (Fixed) */}
      <aside className="hidden md:flex flex-col w-64 border-r border-slate-200 bg-white fixed inset-y-0 left-0 z-20 h-screen font-sans">
        {sidebarNavContent()}
      </aside>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm md:hidden"
            onClick={onMobileClose}
            role="button"
            tabIndex={0}
            aria-label="Close menu"
          />
          <aside className="fixed inset-y-0 left-0 w-64 flex flex-col border-r border-slate-200 bg-white z-50 md:hidden shadow-2xl font-sans h-screen">
            <button
              type="button"
              onClick={onMobileClose}
              className="absolute top-4 right-4 z-10 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
            {sidebarNavContent(onMobileClose)}
          </aside>
        </>
      )}
    </>
  );
}
